import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { PhantomTypeArgument, TypeArgument } from '@/shared/gen/_framework/reified'
import { Decimal } from 'decimal.js'
import {
  PositionConfigInfo,
  PriceInfoObject,
  Amount,
  pythPrice,
  muldiv,
  PositionDecimalMath,
} from '@kunalabs-io/kai'

type Config = ReturnType<
  PositionConfigInfo<PhantomTypeArgument, PhantomTypeArgument, TypeArgument>['configFromBcs']
>
type Pool = ReturnType<
  PositionConfigInfo<PhantomTypeArgument, PhantomTypeArgument, TypeArgument>['poolFromBcs']
>

interface UseLeverageRangeArgs {
  config: Config | undefined
  pool: Pool | undefined
  pioX: PriceInfoObject<PhantomTypeArgument> | undefined
  pioY: PriceInfoObject<PhantomTypeArgument> | undefined
  sanitizedXOnBlur: string
  sanitizedYOnBlur: string
  tickA: number | undefined
  tickB: number | undefined
  isConnected: boolean
}

export const useLeverageRange = ({
  config,
  pool,
  pioX,
  pioY,
  sanitizedXOnBlur,
  sanitizedYOnBlur,
  tickA,
  tickB,
  isConnected,
}: UseLeverageRangeArgs) => {
  const [leverage, setLeverage] = useState<number>(1)
  const handleLeverageChange = useCallback((newValue: number) => {
    setLeverage(newValue)
  }, [])

  const [startL, maxL, maxLeverage] = useMemo(() => {
    if (
      !config ||
      !pool ||
      !pioX ||
      !pioY ||
      (!sanitizedXOnBlur && !sanitizedYOnBlur) ||
      tickA === undefined ||
      tickB === undefined ||
      !isConnected
    ) {
      return [undefined, undefined]
    }
    const currentTick = pool.currentTick()
    if (currentTick < tickA || currentTick >= tickB || tickA >= tickB) {
      return [undefined, undefined]
    }

    const UX = Amount.fromNum(sanitizedXOnBlur || 0, config.info.X.decimals)
    const UY = Amount.fromNum(sanitizedYOnBlur || 0, config.info.Y.decimals)

    const maxL = config.findMaxPositionLiquidity({
      tickA,
      tickB,
      UX,
      UY,
      poolPrice: pool.currentPrice(),
      pythPrice: pythPrice(pioX, pioY),
    })
    const maxLAdjusted = maxL - muldiv(maxL, 20n, 10000n)

    // liquidity at leverage 1
    let startL: bigint
    {
      const pa = pool.tickToPrice(tickA).numeric
      const pb = pool.tickToPrice(tickB).numeric

      const p = pool.currentPrice().numeric
      const lx = PositionDecimalMath.calcLiqFromAmountX(pa, pb, p, new Decimal(UX.int.toString()))
      const ly = PositionDecimalMath.calcLiqFromAmountY(pa, pb, p, new Decimal(UY.int.toString()))

      startL = lx.lt(ly)
        ? BigInt(lx.toFixed(0, Decimal.ROUND_DOWN))
        : BigInt(ly.toFixed(0, Decimal.ROUND_DOWN))
    }

    const P0 = pool.currentPrice().numeric
    const positionMath = PositionDecimalMath.forNewPosition({
      tickA,
      tickB,
      P0,
      UX: new Decimal(UX.int.toString()),
      UY: new Decimal(UY.int.toString()),
      L: new Decimal(maxL.toString()),
    })

    const maxLeverage = positionMath.leverage(P0).toDecimalPlaces(2)

    return [startL, maxLAdjusted, maxLeverage]
  }, [config, pool, pioX, pioY, sanitizedXOnBlur, sanitizedYOnBlur, tickA, tickB, isConnected])

  const prevMaxLeverage = useRef<number | undefined>(undefined)

  useEffect(() => {
    const newMax = maxLeverage?.toNumber()

    // Skip undefined (loading/transitional state) — slider is disabled anyway
    if (newMax === undefined) {
      return
    }

    const oldMax = prevMaxLeverage.current
    prevMaxLeverage.current = newMax

    // "Sticky max": if user was at old max, snap to new max
    const wasAtMax = oldMax !== undefined && leverage >= oldMax - 0.01
    if (wasAtMax) {
      setLeverage(newMax)
      return
    }

    // Clamp if leverage exceeds new max
    if (leverage > newMax) {
      setLeverage(newMax)
    }
  }, [maxLeverage]) // eslint-disable-line react-hooks/exhaustive-deps

  const leverageSliderMarks = useMemo(() => {
    if (!maxLeverage) {
      return []
    }
    const marks = []
    for (let i = 1; i <= maxLeverage.toNumber(); i++) {
      marks.push({
        value: i,
        label: i.toString(),
      })
    }
    return marks
  }, [maxLeverage])

  return {
    leverage,
    handleLeverageChange,
    startL,
    maxL,
    maxLeverage,
    leverageSliderMarks,
  }
}
